import { useEffect, useState } from "react";

// Live countdown to an ISO datetime. Updates every second.
// Renders "LIVE NOW" past kickoff (with `liveLabel`) or a compact `Xd Yh Zm Ws` block.
export default function Countdown({ iso, size = "md", liveLabel = "KICKOFF NOW", className = "" }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!iso) return null;
  const target = new Date(iso).getTime();
  const diff = target - now;
  const past = diff <= 0;

  const totalSec = Math.max(0, Math.floor(diff / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  const scale = {
    sm: { num: "text-xl", lbl: "text-[9px]", gap: "gap-2", pad: "px-2 py-1" },
    md: { num: "text-3xl", lbl: "text-[10px]", gap: "gap-3", pad: "px-3 py-2" },
    lg: { num: "text-4xl md:text-5xl", lbl: "text-[11px]", gap: "gap-4", pad: "px-4 py-3" },
  }[size] || {};

  if (past) {
    return (
      <div
        data-testid="countdown-live"
        className={`inline-flex items-center gap-2 border border-red-500/60 bg-red-500/10 ${scale.pad} ${className}`}
      >
        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
        <span className="font-heading font-black text-red-400 uppercase tracking-widest text-xs">{liveLabel}</span>
      </div>
    );
  }

  const Cell = ({ v, l, testid }) => (
    <div className={`text-center border border-[#232330] bg-[#05050A] ${scale.pad} min-w-[3.25rem]`} data-testid={testid}>
      <div className={`font-heading font-black text-cyan-400 leading-none ${scale.num} tabular-nums`}>
        {String(v).padStart(2, "0")}
      </div>
      <div className={`font-mono uppercase tracking-widest text-gray-500 mt-1 ${scale.lbl}`}>{l}</div>
    </div>
  );

  return (
    <div
      data-testid="countdown"
      className={`inline-flex items-stretch ${scale.gap} ${className}`}
    >
      {days > 0 && <Cell v={days} l="days" testid="countdown-days" />}
      <Cell v={hours} l="hrs" testid="countdown-hours" />
      <Cell v={mins} l="min" testid="countdown-mins" />
      <Cell v={secs} l="sec" testid="countdown-secs" />
    </div>
  );
}
